import { Midpoint } from "NSwag/ServerClient/Client.nswag";
//Lower limit for zoom
const zoomLowerLimit = 0;

//upper limit for zoom
const zoomUpperLimit = 25;

/**
 * Scales an icon based on the current zoom level
 * @param {any} zoomLevel - the current zoom level 0-25
 */
export const scaleIcon = (zoomLevel: number) => {
    if (zoomLevel < zoomLowerLimit || zoomLevel > zoomUpperLimit) {
        throw 'Out of zoom range exception';
    }
    return 0.59 * Math.pow(1.1, zoomLevel - 7);
}

/**
 * Scales a lane based on the current zoom level
 * @param {any} zoomLevel - the current zoom levels 0-25
 */
export const scaleLane = (zoomLevel: number) => {
    if (zoomLevel < zoomLowerLimit || zoomLevel > zoomUpperLimit) {
        throw 'Out of zoom range exception';
    }
    return 0.59 * Math.pow(1.2, zoomLevel - 7) * 2 + .5;
}

interface IPoint {
    tangentBearing: number | null,
    lat: number,
    long: number
}

interface IRawPointData {
    startIndex: number,
    endIndex: number,
    points: IPoint[]
}

/**
 * Generates a bezier curve
 * @param { array } previousMidPoint - an optional point that is adjacent to the start of the line - [lat, long]
 * @param { array } nextMidPoint - an option point that is adjacent to the end of the line - [lat, long]
 * @param { array } points - a list of points to include in the final smoothed line - [ [lat, long], ... ]
 *  where lat and long are an integer in degrees (i.e xx.xxx)
 * @param { integer } laneIndex - the index of the lane, used to determine offset from center lane
 */
export const generateBezierCurve = (points: number[][], laneIndex: number,
    previousMidPoint?: number[], nextMidPoint?: number[]): number[][] => {

    // ToDo: Validate the format of points
    let rawPointData = preparePointData(points, previousMidPoint, nextMidPoint);
    let smoothedPoints = generateSmoothedLine(rawPointData, laneIndex);
    return smoothedPoints;
}

/**
 * A constant to use for generating offset points for a lane if it an outter lane
 */
// ToDo: This needs to be adjusted once the scaling is updated
const LaneWidthMeters = 40;

/**
 * Takes in a list of points for a line and optional points adjancent to start and
 * end points and returns on object containg the start and end index
 * of all points to use in the final smoothed line as well as an array
 * of point objects.
 * @param { array } previousMidPoint - optional point adjacent to the start of the line - [lat, long]
 * @param { array } nextMidPoint - optional point adjacent to the end of the line - [lat, long]
 * @param { array } points - the points to fit the smoothed line to - [ [lat, long], ... ]
 *  where lat and long are an integer
 */
const preparePointData = (points: number[][], previousMidPoint?:
    number[], nextMidPoint?: number[]): IRawPointData  => {

    //make a single list of mid points
    let allPoints = [...points];

    let startIndex = 0;
    // If we have an adjacent segment prior to our start node prepend it
    // and adjust our start index to 1
    if (previousMidPoint) {
        allPoints = [
            previousMidPoint,
            ...allPoints
        ];
        startIndex = 1;
    }

    // If we have an adjacent segment next to our end node append it
    // and adjust our end index to length - 2
    if (nextMidPoint) {
        allPoints = [
            ...allPoints,
            nextMidPoint
        ];
    }

    // set end index
    let endIndex = startIndex + points.length - 1;

    // turn points into array of objects to simplfy access and iterating
    let formattedPoints: IPoint[] = allPoints.map(point => ({ tangentBearing: null, lat: point[0], long: point[1] }));

    return {
        startIndex,
        endIndex,
        points: formattedPoints
    }
}

/**
 * Takes in raw points and the index of the lane to generate a new set
 * of smoothed points, offset by a constant if the lane is not the center lane
 * @param { array } rawPointData - point data in the format [ { lat: xx.xxx, long: xx.xxx, tangentBearing: null }, ... ]
 * @param { integer } laneIndex - 0 based index of the lane
 */
const generateSmoothedLine = (rawPointData: IRawPointData, laneIndex: number) => {

    calculatePointTangents(rawPointData);
    adjustPointsForLaneOffset(rawPointData, laneIndex);
    
    let smoothedPoints: number[][] = [];
    let pointIndex: number;
    let points = rawPointData.points;
    // Calculate control points for every segment
    for (pointIndex = rawPointData.startIndex; pointIndex < rawPointData.endIndex; pointIndex++) {
        let point1 = points[pointIndex];
        let point2 = points[pointIndex + 1];

        let controlPoints = calculateControlPoints(point1, point2);

        let segmentPoints = [
            point1,
            ...controlPoints,
            point2
        ];

        let smoothedSegment = calculateSmoothedPoints(segmentPoints);

        // If not our last point, remove final smoothed point since it will
        // be the first point in the subsequent segment
        if (pointIndex < rawPointData.endIndex - 1) {
            smoothedSegment.splice(-1, 1);
        }

        smoothedPoints.push(...smoothedSegment);
    }

    return smoothedPoints;
}

/**
 * Calcualtes a tangent for each point from start index to end index
 * @param { array } points - Array of point objects [ { lat: xxx, long: xxx }, ... ]
 */
const calculatePointTangents = (rawPointData: IRawPointData) => {

    let startIndex = rawPointData.startIndex;
    let endIndex = rawPointData.endIndex;
    let points = rawPointData.points;

    points.forEach((point: IPoint, index: number) => {

        let previousPoint: IPoint | null = null;
        let nextPoint: IPoint | null = null;

        if (index > 0 && index < points.length - 1) {
            previousPoint = points[index - 1];
            nextPoint = points[index + 1];
        } else {
            // if we do not have 3 points we can not calculate a tangent;
            // edge case of first point and last point will be handled after loop
            return;
        }

        let bearing1 = getBearingBetweenPoints(previousPoint, point);
        let bearing2 = getBearingBetweenPoints(point, nextPoint);
        point.tangentBearing = getTangentBearing(bearing1, bearing2);

    });

    // If the tangent bearing for our first point was skipped flip the bearing from
    // the adjacent point in our line
    let startPoint = points[startIndex];
    if (startPoint.tangentBearing == null) {
        let nextPoint = points[startIndex + 1];
        let bearing = getBearingBetweenPoints(startPoint, nextPoint);
        startPoint.tangentBearing = bearing - (bearing - nextPoint.tangentBearing!);
    }

    // If the tangent bearing for our end point was skipped flip the bearing from
    // the previous point in our line
    let endPoint = points[endIndex];
    if (endPoint.tangentBearing == null) {
        let previousPoint = points[endIndex - 1];
        let bearing = getBearingBetweenPoints(previousPoint, endPoint);
        endPoint.tangentBearing = bearing + (bearing - previousPoint.tangentBearing!);
    }
}

/**
 * If the lane is not the center lane (index 0), adjust all the points
 * for the geometry to the right by a constant to offset where they render from the center lane
 * @param {any} rawPointData
 */
const adjustPointsForLaneOffset = (rawPointData: IRawPointData, laneIndex: number) => {

    if (laneIndex > 0) {
        rawPointData.points.forEach(point => {
            let offsetBearing = normalizeBearing(point.tangentBearing! + 90);
            let offsetDistance = ((LaneWidthMeters + .5) * laneIndex + 2) * .001;
            let offsetPoint = getDestinationPoint(offsetBearing, offsetDistance, point);

            // update lat and long to offset
            point.lat = offsetPoint.lat;
            point.long = offsetPoint.long;
        });
    }
}

/**
 *
 * Calculates the control points for a quadratic bezier curve
 * for two points that know their tangents.
 * @param {any} point1
 * @param {any} point2
 */
const calculateControlPoints = (point1: IPoint, point2: IPoint): IPoint[] => {

    let distanceKm = getDistanceBetweenPoints(point1, point2);
    let controlPointOffset = distanceKm / 4;
    let controlPoint1 = getDestinationPoint(point1.tangentBearing!, controlPointOffset, point1);
    let controlPoint2 = getDestinationPoint(point2.tangentBearing!, -controlPointOffset, point2);

    return [ controlPoint1, controlPoint2 ];
}

/**
 *  Caclualtes points to render a quadratic bezier curve
 *  given a 4 points.
 * @param { array } points - array of 4 points where a point is { lat: xx.xxx, long: xx.xxxx }
 */
const calculateSmoothedPoints = (points: IPoint[]): number[][] => {
    let stepRate = .2;

    let smoothedPoints: number[][] = [];

    let step: number;
    for (step = 0; step <= 1; step += stepRate) {
        let lat = Math.pow((1 - step), 3) * points[0].lat +
            (3 * Math.pow((1 - step), 2) * step * points[1].lat) +
            (3 * (1 - step) * Math.pow(step, 2) * points[2].lat) +
            (Math.pow((step), 3) * points[3].lat);

        let long = Math.pow((1 - step), 3) * points[0].long +
            (3 * Math.pow((1 - step), 2) * step * points[1].long) +
            (3 * (1 - step) * Math.pow(step, 2) * points[2].long) +
            (Math.pow((step), 3) * points[3].long);

        smoothedPoints.push([lat, long]);
    }

    return smoothedPoints;
}

/**
 * Calculate the bearing between two positions as a value from -180 to 180
 *
 * @param point1 - The start point - { lat: xxx, long: xxx}
 * @param point2 - The end point - { lat: xxx, long: xxx}
 * @return int - The bearing between 0 and 360
 */
function getBearingBetweenPoints(point1: IPoint, point2: IPoint) {
    let dLon = toRadians(point2.long - point1.long);
    let y = Math.sin(dLon) * Math.cos(toRadians(point2.lat));
    let x = Math.cos(toRadians(point1.lat)) * Math.sin(toRadians(point2.lat)) - Math.sin(toRadians(point1.lat)) * Math.cos(toRadians(point2.lat)) * Math.cos(dLon);
    let bearing = toDegrees(Math.atan2(y, x));
    return normalizeBearing((bearing + 360) % 360);
}

/**
 * Gets the tangent of two bearings ranging from -180 to 180
 * @param {any} firstBearing
 * @param {any} secondBearing
 */
const getTangentBearing = (firstBearing: number, secondBearing: number) => {

    // Adjust bearings to be from -180 to 180
    let bearing1 = normalizeBearing(firstBearing);
    let bearing2 = normalizeBearing(secondBearing);

    let delta = bearing1 - bearing2;


    // Adjust delta of angles to handle edge cases
    if (delta < -180) {
        delta = 360 % delta;
    } else if (delta > 180) {
        delta = -(360 % delta);
    }

    let rawTangent = bearing1 - (delta / 2);

    let tangentBearing = normalizeBearing(rawTangent);

    return tangentBearing;
}

/**
 *  Normalize a bearing to be within range of -180 to 180
 * @param {any} bearing
 */
const normalizeBearing = (bearing: number) => {
    // gives us a true bearing from 0-360
    let normalizedBearing = bearing % 360;

    if (bearing > 180) {
        return -180 + (normalizedBearing % 180);
    } else {
        return normalizedBearing;
    }
}

/**
 * Gets the distance between two points in km
 */
const getDistanceBetweenPoints = (point1: IPoint, point2: IPoint) => {
    let R = 6371; // earth's mean radius in km

    let startPoint = {
        lat: toRadians(point1.lat),
        long: toRadians(point1.long)
    }

    let endPoint = {
        lat: toRadians(point2.lat),
        long: toRadians(point2.long)
    }

    let dLat = endPoint.lat - startPoint.lat;
    let dLon = endPoint.long - startPoint.long;
    let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(startPoint.lat) * Math.cos(endPoint.lat) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Calculates destination point given start point, initial bearing (deg), and distance(km)
 * @param {any} bearing - degree
 * @param {any} distance - distance in km
 * @param {any} point - an object with {lat: , lng:}
 */
const getDestinationPoint = (bearing: number, distance: number, point: IPoint, log = false): IPoint => {

    let R = 6371; //earth's mean radius in km

    let startPoint = {
        lat: toRadians(point.lat),
        long: toRadians(point.long)
    };

    let bearingInRadians = toRadians(bearing);

    let destinationLat = Math.asin(Math.sin(startPoint.lat) * Math.cos(distance / R) + Math.cos(startPoint.lat) * Math.sin(distance / R) * Math.cos(bearingInRadians));
    let destinationLong = startPoint.long +
        Math.atan2(Math.sin(bearingInRadians) * Math.sin(distance / R) * Math.cos(startPoint.lat),
            Math.cos(distance / R) - Math.sin(startPoint.lat) * Math.sin(destinationLat));
    return {
        tangentBearing: null,
        lat: toDegrees(destinationLat),
        long: toDegrees(destinationLong)
    };;
}

/**
 * Converts degrees to radians
 * @param {any} degree
 */
function toRadians(degree: number) {
    return degree * (Math.PI / 180);
}
/**
 * Converts radian to degree
 * @param {any} radian
 */
function toDegrees(radian: number) {
    return radian * (180 / Math.PI);
}